// Fairfield University: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Fairfield's Common Data Set 2025-2026 (Institutional Research; the
//    September 21, 2026 file on the Common Data Sets page, which replaced a
//    March file whose page headers still read 2024-2025): C1 applicants,
//    admits and enrollees for Fall 2025 with the residency table, C8 test
//    policy for Fall 2027 and the score date, C21 early decision counts
//    (labelled "For the Fall 2025 entering class"), C22 early action. Its G1
//    is marked as not final for 2026-2027 ("Check here if your institution's
//    2026-2027 academic year costs of attendance are not available"), so the
//    cost comes from the university's 2026-27 Estimated Cost of Attendance
//    on the Undergraduate Tuition page.
//  - fairfield.edu admissions: First-Year Applicants (dates, decision
//    notification, application types, test-optional policy and FAQs).
//  - The 2026-2027 Undergraduate Catalog's Admission page (subject
//    preparation for nursing, math, engineering, business and the sciences).
//  - College Scorecard API, school id 129242 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Fairfield Common Data Set 2025-2026',
        url: 'https://www.fairfield.edu/_files/documents/pdfs/4447623414_about_offices-and-departments_institutional-research_common-data-sheet-2025-26_09212026.pdf',
        read: '2026-10-01',
    },
    apply: {
        label: 'Fairfield Admission, First-Year Applicants',
        url: 'https://www.fairfield.edu/admission-and-aid/apply/first-year-applicants/index.html',
        read: '2026-10-01',
    },
    catalog: {
        label: 'Fairfield Undergraduate Catalog 2026-2027, Admission',
        url: 'https://catalog.fairfield.edu/undergraduate/admission/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Fairfield Tuition and Costs, Undergraduate Tuition (2026-27)',
        url: 'https://www.fairfield.edu/admission-and-aid/tuition-and-costs/undergraduate/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?129242-Fairfield-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'fairfield-university',
    kbId: 'fairfield_university',
    name: 'Fairfield University',
    shortName: 'Fairfield',
    location: 'Fairfield, CT',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Fairfield brand resources); decoration only.
    branding: {
        primary: '#C8102E',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/fairfield_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 21299,
        admitted: 5336,
        enrolled: 1513,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 3411, admitted: 672 },
            { group: 'Out-of-state', applied: 17156, admitted: 4624 },
            { group: 'International', applied: 732, admitted: 40 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Fairfield\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        year: 'Fall 2025',
        note: 'The Common Data Set reports Early Decision I and II together. Fairfield also has a nonbinding early action plan, for which it gives no counts.',
        rows: [
            { plan: 'Early Decision I and II', applied: 689, admitted: 566 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Fairfield as a whole, through the Common App. The catalog asks for some subject preparation:',
        rows: [
            { school: 'Nursing', requirement: 'Candidates for nursing must have one high school laboratory course in chemistry.' },
            { school: 'Math, engineering, business and the sciences', requirement: 'Candidates are urged to take a fourth unit of lab science and of math, preferably precalculus or calculus.' },
        ],
        source: 'catalog',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'December 20 to January 15', binding: false },
            { plan: 'Early Decision I', deadline: 'November 15', decision: 'December 15', binding: true },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'April 1', binding: false },
        ],
        note: 'The FAFSA and CSS Profile are due December 1 for Early Action and Early Decision I, and January 15 for Early Decision II and Regular Decision. Your counselor\'s transcript, school report and recommendation are accepted up to two weeks after the deadline. The application fee is $65. You can switch from Early Action to Early Decision I until December 1, and from a deferred Early Action or a Regular Decision application to Early Decision II until January 27.',
        source: 'apply',
        binding: 'Early Decision I and II are binding: if you are admitted, you withdraw all other college applications and confirm that you will attend Fairfield. Switching to Early Decision means sending Fairfield an Early Decision agreement form.',
        bindingSource: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Fairfield does not require the SAT or ACT, and students who do not send scores have the same opportunities for admission and merit scholarships. If you want scores considered, you can self-report them on the Common App, add them in your applicant portal, or have them sent by the testing agency; students who enroll send an official report. Homeschooled applicants must still send SAT or ACT results.',
        source: 'apply',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 61390 },
            { item: 'General fee', amount: 1025 },
            { item: 'Orientation fee (first-year students)', amount: 425 },
            { item: 'Housing (on campus)', amount: 13134 },
            { item: 'Food', amount: 8430 },
            { item: 'Books, class materials, supplies and equipment', amount: 1175 },
            { item: 'Transportation', amount: 1150 },
            { item: 'Miscellaneous expenses', amount: 1500 },
        ],
        total: 88229,
        note: 'Fairfield\'s estimated cost of attendance for a first-year student living on campus. Housing is the average on-campus rate and food is a three-meals-a-day plan. All full-time undergraduates must also carry health insurance, which is not in the total.',
        source: 'costs',
        netPrice: {
            average: 49906,
            byIncome: [
                { income: '$0 to $30,000', amount: 30752 },
                { income: '$30,001 to $48,000', amount: 35919 },
                { income: '$48,001 to $75,000', amount: 37768 },
                { income: '$75,001 to $110,000', amount: 42341 },
                { income: 'Over $110,000', amount: 53583 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Fairfield\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
